import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/useAuthStore';
import {
    clearPwaHandoffCookie, isStandaloneDisplay, readPwaHandoffCookie, redeemPwaLoginHandoff,
} from '../../lib/pwaLoginHandoff';

// 아이폰 홈 화면 앱 첫 실행 — 사파리에서 받아 둔 한 번용 코드로 로그인을 이어 받고, 보던 분석으로 한 번 이동한다
// (대표 10/28 "가능하면 꼭 진행해", Readme/PwaLoginHandoff.md). 코드가 없으면(평소 실행·알림으로 연 실행) 아무것도 안 한다.
// 실패하면 로그인 화면으로 보내고, 로그인 뒤 그 분석으로 한 번 이어진다(?next=).
const DEFAULT_RESUME = '/visitor-analysis?resume=1';

export const PwaLoginHandoff: React.FC = () => {
    const navigate = useNavigate();
    const isInitialized = useAuthStore((state) => state.isInitialized);
    const user = useAuthStore((state) => state.user);
    const verifyOtp = useAuthStore((state) => state.verifyOtp);
    const startedRef = useRef(false);
    // 첫 화면이 그려지기 전에 가림막을 띄운다(앱이 쿠키를 읽을 수 있을 때만)
    const [working, setWorking] = useState(() => isStandaloneDisplay() && readPwaHandoffCookie() !== null);

    useEffect(() => {
        if (!working || startedRef.current || !isInitialized) return;
        startedRef.current = true;
        const handoff = readPwaHandoffCookie();
        clearPwaHandoffCookie(); // 한 번만 — 성공·실패와 관계없이
        if (!handoff) { setWorking(false); return; }
        const resumePath = handoff.resumePath ?? DEFAULT_RESUME;

        (async () => {
            // 이미 이 앱에 로그인돼 있으면(예전에 설치해 쓰던 앱) 보던 분석으로만 이동한다
            if (user) {
                navigate(resumePath, { replace: true });
                return;
            }
            let result: 'signed-in' | 'failed' = 'failed';
            try {
                result = await redeemPwaLoginHandoff(handoff.code, verifyOtp);
            } catch (error) {
                console.warn('[PwaLoginHandoff] 이어 받기 실패:', error instanceof Error ? error.message : String(error));
            }
            navigate(result === 'signed-in' ? resumePath : `/auth?next=${encodeURIComponent(resumePath)}`, { replace: true });
        })().finally(() => setWorking(false));
    }, [working, isInitialized, user, navigate, verifyOtp]);

    if (!working) return null;

    return (
        <div
            className="fixed inset-0 z-[10000] flex flex-col items-center justify-center gap-4 bg-[#0B0B0B] px-8 text-center"
            role="status"
            aria-live="polite"
        >
            <span className="h-9 w-9 rounded-full border-2 border-white/15 border-t-[#3182F6] animate-spin" aria-hidden />
            <div>
                <p className="text-[17px] font-semibold text-white">로그인을 이어 받고 있어요</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-[#A8A8A8] break-keep">사파리에서 보던 분석을 이어서 열어 드릴게요</p>
            </div>
        </div>
    );
};

export default PwaLoginHandoff;
